import React, { useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import styles from "./detail.module.css";
import Layout from "../../components/layout/layout";
import Output from "../../components/output/output";
import LineGraph from "./components/line_graph/line_graph";
import { useParams } from "react-router-dom";
import axiosInstance from "../../AxiosInstance";
import OutputGraphicGroup from "./components/output_graphic_group/output_graphic_group";
import DetailsHeader from "./components/details_header/details_header";
import UptimeCounter from "../../components/uptime_counter/uptime_counter";
import MainSelect from "./components/main_select/main_select";
import TemperatureSection from "./components/sections/temperature_section/temperature_section";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import AlertSection from "./components/sections/alert_section/alert_section";
import UsageSection from "./components/sections/usage_section/usage_section";
import LogsSection from "./components/sections/logs_section/logs_section";
import NetworkSection from "./components/sections/network_section/network_section";
import FirmwareSection from "./components/sections/firmware_section/firmware_section";
import HistorySection from "./components/sections/history_section/history_section";
import ElectricalSection from "./components/sections/electrical_section/electrical_section";
import ParamsSection from "./components/sections/params_section/params_section";
import OutputSettingsModal from "./modals/output_settings";
import OutputStatesModal from "./modals/output_states";
import ResetAllModal from "./modals/reset_all";
import Loader from "../../components/loader/loader";
import RestartModal from "./modals/restart_pdui";
import { height } from "@fortawesome/free-solid-svg-icons/fa0";
import useSecretTap from "../../hooks/useSecretTap";
import usePolling from "../../hooks/usePolling";
import DiagnosticsPanel from "../../components/diagnostics_panel/DiagnosticsPanel";
import { reportPlatformAlert } from "../../platformAlerts";

// El detalle muestra el estado de los relés, así que refresca seguido para que
// encender/apagar un puerto se vea reflejado rápido. Temperatura y alertas
// cambian mucho más lento y solo se consultan con su sección abierta.
const PDUI_POLL_MS = 3000;
const TEMPERATURE_POLL_MS = 10000;
const ALERT_POLL_MS = 5000;
// Página en vivo de alertas; las anteriores se piden desde la sección (Ver anteriores).
export const ALERT_PAGE = 30;
// Fallos seguidos del poll principal a partir de los cuales, al recuperarse,
// se deja una alerta de plataforma en el equipo (≈15 s sin API).
const API_FAILURE_STREAK_FOR_ALERT = 5;

export default function PDUIDetail() {
  const { t } = useTranslation();
  const { pdui_id } = useParams();
  const [outputs, setOutputs] = useState([]);
  const [temperatures, setTemperatures] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState(-1);
  const [selectedSection, setSelectedSection] = useState(1);
  const [activeTab, setActiveTab] = useState("outputs");
  const [pduiData, setPduiData] = useState({});
  const [recentEmergency, setRecentEmergency] = useState(false);
  const [loading, setLoading] = useState(true);
  const { unlocked: diagnosticsUnlocked, register: registerDiagnosticsTap, lock: lockDiagnostics } = useSecretTap(5, 2000);
  const apiFailures = useRef({ count: 0, since: null });

  usePolling(
    (isActive) =>
      axiosInstance
        .get(`pdui/${pdui_id}/`)
        .then((response) => {
          if (!isActive() || response?.status !== 200) {
            return;
          }
          // Se avisa al recuperarse, no durante el corte: mientras la API no
          // responde tampoco se podría registrar la alerta.
          const failures = apiFailures.current;
          if (failures.count >= API_FAILURE_STREAK_FOR_ALERT) {
            const seconds = Math.round((Date.now() - failures.since) / 1000);
            reportPlatformAlert(
              Number(pdui_id),
              "api_unreachable",
              `La plataforma estuvo ${seconds} s sin respuesta del servidor (${failures.count} consultas fallidas seguidas)`,
            );
          }
          apiFailures.current = { count: 0, since: null };
          setPduiData(response.data);
          let relays = response.data.relays;
          if (relays && relays.voltages) {
            relays["status"] = relays.voltages.map((voltage) => voltage > 2);
            relays["ignition_status"] = response.data.ignition_status;
            if (response.data.latest) {
              setOutputs(relays);
            }
          }
        })
        .catch((error) => {
          const failures = apiFailures.current;
          apiFailures.current = {
            count: failures.count + 1,
            since: failures.since || Date.now(),
          };
          console.error("Error fetching PDUI data:", error);
        })
        .finally(() => {
          if (isActive()) setLoading(false);
        }),
    PDUI_POLL_MS
  );

  usePolling(
    (isActive) =>
      axiosInstance
        .get(`pdui/${pdui_id}/temperature/`)
        .then((response) => {
          if (!isActive() || response?.status !== 200) {
            return;
          }
          setTemperatures(response.data);
        })
        .catch((error) => {
          console.error("Error fetching temperature data:", error);
        }),
    TEMPERATURE_POLL_MS,
    selectedSection === 3
  );

  usePolling(
    (isActive) =>
      axiosInstance
        .get(`alert/?pdui_id=${pdui_id}&limit=${ALERT_PAGE}`)
        .then((response) => {
          if (!isActive() || response?.status !== 200) {
            return;
          }
          setAlerts(response.data);
          let found = false;
          response.data.forEach((alert) => {
            if (alert.alert_type === 5) {
              // if alert.created_at is less than 10 seconds ago
              let now = new Date();
              let alertDate = new Date(alert.created_at);
              let diff = (now - alertDate) / 1000;
              if (diff < 10) {
                found = true;
                setRecentEmergency(true);
              }
            }
          });
          if (!found) {
            setRecentEmergency(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching alerts:", error);
        }),
    ALERT_POLL_MS,
    selectedSection === 2
  );

  const getSection = () => {
    if (selectedSection === 1) {
      return <UsageSection selectedSection={selectedSection} />;
    } else if (selectedSection === 2) {
      return (
        <AlertSection
          pduiData={pduiData}
          alerts={alerts}
          pdui_id={pdui_id}
          setRecentEmergency={setRecentEmergency}
          selectedSection={selectedSection}
        />
      );
    } else if (selectedSection === 3) {
      return (
        <TemperatureSection
          pduiData={pduiData}
          temperatures={temperatures}
          selectedSection={selectedSection}
        />
      );
    } else if (selectedSection === 4) {
      return <LogsSection pdui_id={pdui_id} />;
    } else if (selectedSection === 5) {
      return <NetworkSection pdui_id={pdui_id} />;
    } else if (selectedSection === 6) {
      return <FirmwareSection pdui_id={pdui_id} pduiData={pduiData} />;
    } else if (selectedSection === 7) {
      return <HistorySection pdui_id={pdui_id} />;
    } else if (selectedSection === 8) {
      return <ElectricalSection pdui_id={pdui_id} />;
    } else if (selectedSection === 9) {
      return <ParamsSection pdui_id={pdui_id} />;
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {loading && <Loader />}
      <Layout selectedIndex={1}>
      <OutputStatesModal />
      <OutputSettingsModal />
      <ResetAllModal />
      {pduiData.can_restart && <RestartModal />}
      <DetailsHeader pdui_id={pdui_id} pduiData={pduiData} onSecretTap={registerDiagnosticsTap} />
      {diagnosticsUnlocked && (
        <DiagnosticsPanel pduiId={pdui_id} onClose={lockDiagnostics} />
      )}
      <MainSelect
        recentEmergency={recentEmergency}
        data={outputs}
        pduiData={pduiData}
        setSelectedSection={setSelectedSection}
        selectedSection={selectedSection}
        setSelectedOutput={setSelectedOutput}
        setActiveTab={setActiveTab}
        statusSlot={Object.keys(pduiData).length > 0 ? <UptimeCounter pduiData={pduiData} /> : null}
      />
      {Object.keys(pduiData).length > 0 && (
        <>
          {/* Tabs for mobile/tablet */}
          <div className={styles.tabBar}>
            <div
              className={`${styles.tab} ${activeTab === "outputs" ? styles.tabActive : ""}`}
              onClick={() => setActiveTab("outputs")}
            >
              {t('detail.outputs')}
            </div>
            <div
              className={`${styles.tab} ${activeTab === "details" ? styles.tabActive : ""}`}
              onClick={() => setActiveTab("details")}
            >
              {t('detail.details')}
            </div>
          </div>

          <div className={styles.container}>
            <div className={`${styles.outputContainer} ${activeTab === "outputs" ? styles.activeColumn : ""}`}>
              {Array.from(
                { length: pduiData?.model?.port_count },
                (_, i) => i + 1,
              ).map((i) => (
                <Output
                  onClick={() => {
                    setSelectedOutput(i);
                    setSelectedSection(-1);
                    setActiveTab("details");
                  }}
                  data={outputs}
                  key={i}
                  number={i}
                  className={"mb-2"}
                  pdui_id={pdui_id}
                  pduiData={pduiData}
                />
              ))}
            </div>
            <div className={`${styles.detailContainer} ${activeTab === "details" ? styles.activeColumn : ""}`}>
              <div className={"d-flex justify-content-start pb-3"}>
                <FontAwesomeIcon
                  icon={faX}
                  color={"#6A86C8"}
                  size={"lg"}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedSection(1);
                    setSelectedOutput(-1);
                    setActiveTab("outputs");
                  }}
                />
              </div>
              {selectedOutput !== -1 || selectedSection === 1 ? (
                <OutputGraphicGroup
                  selectedOutput={selectedOutput}
                  pdui_id={pdui_id}
                />
              ) : (
                getSection()
              )}
            </div>
          </div>
        </>
      )}
      </Layout>
    </div>
  );
}
